import { Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useSuspenseApiMemberEnv } from '@repo/api-codegen';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { useNavigate } from '@repo/common/hooks/useNavigate';
import { titleCase } from '@repo/common/utils';
import { Text } from '@repo/foundations';
import { useCallback } from 'react';
import {
	integrationsQueryKeyFactory,
	useCreateIntegration,
	useIntegrationList,
	useSuspenseFeatureAccess,
} from '../../../api';
import { useMarketplaceIntegrationSpecList } from '../../../api/hooks/marketplace';
import { useLimits } from '../../../api/hooks/workspace/useLimits';
import { usePlan } from '../../../hooks/usePlans';
import type { IntegrationSpec } from '../../../interfaces/IntegrationSpec';
import { IS_LOCAL_OR_DEVELOPMENT } from '../../../utils/envs';
import { useFeatureFlags } from '../../../utils/featureFlags';
import { useIntercomWithDomainCheck } from '../../../utils/useIntercomWithDomainCheck';
import { IntegrationSpecCard } from '../../IntegrationSpecCard/IntegrationSpecCard';
import {
	closeAllModals,
	closeModal,
	openConfirmModal,
} from '../../ModalManager';
import { PricingDetails } from '../../PriceIncreaseModals/PricingDetails';
import WidgetSelector from '../../WidgetSelector';
import type { WidgetData } from '../../WidgetSelector/types';
import {
	checkIntegrationLimits,
	shouldShowIntegrationWithFeatureFlag,
} from './AddIntegrationModal.helpers';
import { useStyles } from './AddIntegrationModal.styles';
import { NEW_INTEGRATION_MODAL_ID } from './constants';

function AddIntegrationModal({
	modalType,
	integrationTypes,
	title = 'Connect integration',
}: {
	integrationTypes?: string[];
	title?: string;
	modalType: 'integration' | 'extension';
}) {
	const { theme, classes } = useStyles();

	const navigate = useNavigate();
	const { isPayingCustomer, trial } = usePlan();
	const {
		usedIntegrationSeats,
		trialIntegrationsLimit,
		trialExtensionsLimit,
		integrationSeats,
		usedExtensionSeats,
		extensionSeats,
	} = useLimits();
	const { data: memberEnv } = useSuspenseApiMemberEnv({});
	const featureAccess = useSuspenseFeatureAccess();

	const { data: integrations } = useIntegrationList({});

	const { data: marketplaceIntegrationSpecs } =
		useMarketplaceIntegrationSpecList({
			filters: { browsing: true },
			options: {
				enabled: !!featureAccess.secodaCoreAccess,
				select: (res) => res.results,
			},
		});

	const { azureDataLakeIntegration } = useFeatureFlags();
	const allFeatureFlags = useFeatureFlags();

	const builtinIntegrationSpecsData = integrationList
		.filter(
			(integration) =>
				(!integration.hidden &&
					integration.secodaEnabled &&
					(memberEnv?.SLACK_ENABLED || integration.type !== 'slack')) ||
				integration.type === 'marketplace'
		)
		.filter(
			(integration) => integrationTypes?.includes(integration.type) ?? true
		)
		.filter(
			(integration) =>
				integration.type !== 'datalake' || azureDataLakeIntegration
		)
		.filter((integration) => {
			// If the integration has secodaCoreEnabled set to true, we need to check if the user has access to it with secodaCoreAccess feature access
			if (integration.secodaCoreEnabled === true) {
				return featureAccess.secodaCoreAccess;
			}
			// If the integration has secodaCoreEnabled set to false or undefined, we allow it by default
			return true;
		})
		.filter((integration) =>
			shouldShowIntegrationWithFeatureFlag({
				integration,
				featureFlags: allFeatureFlags,
				existingIntegrations: integrations,
			})
		)
		.map((integration) => ({
			group: integration.group,
			title: integration.name,
			data: { type: 'builtin', value: integration } as IntegrationSpec,
		}));

	const marketplaceIntegrationSpecsData = (marketplaceIntegrationSpecs || [])
		.filter((spec) => spec.versions.length > 0)
		.map((spec) => ({
			group: titleCase(spec.versions[0].category.toLowerCase()).replace(
				'_',
				' '
			),
			title: spec.versions[0].name,
			data: { type: 'marketplace', value: spec } as IntegrationSpec,
			key: spec.id,
		}));

	const data = [
		...builtinIntegrationSpecsData,
		...marketplaceIntegrationSpecsData,
	];

	const { mutateAsync: createIntegration } = useCreateIntegration({
		invalidationKeys: [integrationsQueryKeyFactory.allLists()],
	});

	const { boot, show } = useIntercomWithDomainCheck();

	const openIntercom = useCallback(async () => {
		await boot();
		show();
	}, [boot, show]);

	const createCustomIntegration = useCallback(async () => {
		// In production, trial users and non-paying customers can't create custom
		// integrations.
		if (!IS_LOCAL_OR_DEVELOPMENT && (!isPayingCustomer || trial)) {
			showNotification({
				title: 'Custom integrations are not available in your plan.',
				message:
					'Please upgrade to a paying plan to create custom integrations.',
				color: 'red',
			});
			return;
		}
		const integration = await createIntegration({
			data: {
				name: 'Untitled',
				type: 'custom',
				credentials: {},
			},
		});

		navigate(`/integrations/${integration.id}/`);
	}, [createIntegration, isPayingCustomer, navigate, trial]);

	const handleIntegrationSelect = async (
		widgetData: WidgetData<IntegrationSpec>
	) => {
		const { type, value } = widgetData.data;

		const handleNavigation = async () => {
			closeModal(NEW_INTEGRATION_MODAL_ID);

			if (type === 'builtin') {
				if (value.type === 'custom') {
					await createCustomIntegration();
				} else if (value.type === 'marketplace') {
					navigate('/marketplace-integration/new');
				} else {
					navigate(`/integrations/new?type=${value.type}`);
				}
			} else {
				navigate(`/integrations/new?selectedMarketplaceSpec=${value.id}`);
			}
		};

		const { showIntegrationLimitReachedModal, showExtensionLimitReachedModal } =
			checkIntegrationLimits({
				newIntegrationInterfaceType: type,
				newIntegrationType: 'type' in value ? value.type : undefined,
				integrations,
				trial,
				usedIntegrationSeats,
				integrationSeats,
				usedExtensionSeats,
				extensionSeats,
				trialIntegrationsLimit,
				trialExtensionsLimit,
			});

		if (
			(modalType === 'integration' && showIntegrationLimitReachedModal) ||
			(modalType === 'extension' && showExtensionLimitReachedModal)
		) {
			if (trial) {
				openConfirmModal({
					title: 'Trial limit reached',
					children: (
						<Stack>
							<Text>
								You&apos;ve reached the limit for your trial. Contact support to
								upgrade to a paid plan.
							</Text>
						</Stack>
					),
					labels: {
						cancel: 'Cancel',
						confirm: 'Upgrade',
					},
					onConfirm: () => {
						closeAllModals();
						openIntercom();
					},
					onCancel: closeAllModals,
				});
			} else {
				openConfirmModal({
					title: 'Plan limit reached',
					children: (
						<Stack>
							<PricingDetails
								type="integrations"
								nominalCurrentCount={usedIntegrationSeats}
								nominalChange={1}
							/>
							<Text size="xs">
								Your account will be charged for 1 additional Integration seat
								after the configuration wizard is complete
							</Text>
						</Stack>
					),
					labels: {
						cancel: 'Cancel',
						confirm: 'Upgrade',
					},
					onConfirm: handleNavigation,
					onCancel: closeAllModals,
				});
			}
			return;
		} else {
			await handleNavigation();
		}
	};

	return (
		<WidgetSelector
			title={title}
			data={data}
			type="filter"
			onWidgetSelect={handleIntegrationSelect}
			widgetGroupProps={{
				classNames: {
					widgetItemsWrapper: classes.widgetItemsWrapper,
					widgetWrapper: classes.widgetWrapper,
				},
			}}
			scrollAreaHeight={theme.other.space[160]}
			cols={3}
			renderer={IntegrationSpecCard}
		/>
	);
}

export default AddIntegrationModal;
